/**
 * LocaleHead — locale-aware SEO head rels (issue #221 PR #2, Step 5).
 *
 * Mounted INSIDE the Router so it can read the active route. On every path
 * change it emits, into `<head>`:
 *   - `<link rel="canonical">` pointing at the SELF locale URL (never
 *     cross-locale — that would tell Google to dedupe languages together);
 *   - one `<link rel="alternate" hreflang="…">` per supported locale, reciprocal
 *     (the active locale lists itself), plus `x-default` → the store default.
 * It also keeps `<html lang>` aligned with the active locale (screen readers and
 * Google both key off it).
 *
 * Canonical/alternate hrefs intentionally carry only origin + path (no query
 * string) so volatile params don't fork the canonical. The set of managed links
 * is fully cleared + re-emitted each render (marked with `data-locale-head`),
 * which keeps the logic trivially correct as the path changes.
 *
 * hreflang attribute values use the catalog's canonical BCP-47 form (`sv-SE`);
 * the href path segment is lowercase (`/sv-se/…`) per the URL contract (TD-1).
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { readLocaleCatalog } from "./localeCatalog";
import { splitLocaleFromPath, withLocale } from "./localeRouting";

const MARK = "data-locale-head";

const LocaleHead: React.FC = () => {
	const location = useLocation();

	useEffect(() => {
		if (typeof document === "undefined" || typeof window === "undefined") return;

		const { supported, defaultLocale } = readLocaleCatalog();
		const origin = window.location.origin;
		const { locale: urlLocale, rest } = splitLocaleFromPath(location.pathname, supported);
		const activeLocale = urlLocale ?? defaultLocale;

		// <html lang> — a11y (screen readers) + SEO.
		document.documentElement.lang = activeLocale;

		// Clear previously-managed rels, then re-emit for the current path.
		document.head.querySelectorAll(`link[${MARK}]`).forEach((el) => el.remove());

		const appendLink = (rel: string, hreflang: string | null, href: string) => {
			const link = document.createElement("link");
			link.setAttribute("rel", rel);
			if (hreflang) link.setAttribute("hreflang", hreflang);
			link.setAttribute("href", href);
			link.setAttribute(MARK, "");
			document.head.appendChild(link);
		};

		const localeHref = (loc: string) => `${origin}${withLocale(loc, rest)}`;

		// Self-referential canonical (per-locale).
		appendLink("canonical", null, localeHref(activeLocale));
		// One alternate per supported locale (reciprocal — includes the active one).
		for (const entry of supported) {
			appendLink("alternate", entry.locale, localeHref(entry.locale));
		}
		// x-default → store default.
		appendLink("alternate", "x-default", localeHref(defaultLocale));
		// Deps: pathname only — the catalog (readLocaleCatalog) is boot-immutable
		// (read synchronously from getCachedConfig(), which never changes after the
		// App's config-load gate), so re-running on path change captures every
		// catalog value. Do not add `supported`/`defaultLocale` here.
	}, [location.pathname]);

	return null;
};

export default LocaleHead;
